import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { Lock, Zap, Package, Radio } from "lucide-react";

const steps = [
  { key: "step1", icon: Lock, dur: 1800 },
  { key: "step2", icon: Zap, dur: 1600 },
  { key: "step3", icon: Package, dur: 1700 },
  { key: "step4", icon: Radio, dur: 1500 },
];

const hex = (n) =>
  Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join("");

export default function EncryptionSequence() {
  const { t } = useLang();
  const { tid } = useParams();
  const nav = useNavigate();
  const [active, setActive] = useState(0);
  const [done, setDone] = useState([]);
  const [telemetry, setTelemetry] = useState([]);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const run = async () => {
      for (let i = 0; i < steps.length; i++) {
        if (cancelled.current) return;
        setActive(i);
        await new Promise((r) => setTimeout(r, steps[i].dur));
        setDone((d) => [...d, i]);
      }
      // small dwell then go to command center
      await new Promise((r) => setTimeout(r, 500));
      if (!cancelled.current) nav(`/transmission/${tid}`);
    };
    run();
    const tm = setInterval(() => {
      setTelemetry((arr) => {
        const next = [
          `${new Date().toISOString().split("T")[1].replace("Z", "")} · ${hex(6).toUpperCase()} · ${(Math.random() * 4000).toFixed(2)} kbps`,
          ...arr,
        ].slice(0, 10);
        return next;
      });
    }, 220);
    return () => { cancelled.current = true; clearInterval(tm); };
  }, [tid, nav]);

  return (
    <div className="min-h-screen bg-[#03040B] text-white relative overflow-hidden grid-bg" data-testid="encryption-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(0,212,255,0.12), transparent 60%)" }} />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-24">
        <div className="label-overline mb-2">Stage 3 / Mission · {tid}</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter" data-testid="encryption-title">{t("enc.title")}</h1>

        <div className="mt-14 relative">
          <div className="absolute left-6 top-2 bottom-2 w-px bg-[#00D4FF]/20" />
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === active && !done.includes(i);
            const isDone = done.includes(i);
            return (
              <div key={s.key} className="flex items-start gap-6 mb-8 relative" data-testid={`encrypt-step-${i}`}>
                <div className={`w-12 h-12 grid place-items-center border ${
                  isDone ? "border-[#00FFAA] bg-[#00FFAA]/10" :
                  isActive ? "border-[#00D4FF] pulse-glow" : "border-white/15"
                }`}>
                  <Icon size={18} className={isDone ? "text-[#00FFAA]" : isActive ? "text-[#00D4FF]" : "text-white/40"} />
                </div>
                <div className="flex-1 pt-1">
                  <div className={`font-display font-bold tracking-tight text-lg ${
                    isDone ? "text-[#00FFAA]" : isActive ? "text-white text-cyan-glow" : "text-white/40"
                  }`}>{t(`enc.${s.key}`)}</div>
                  <div className="h-[3px] w-full bg-white/5 mt-3 relative overflow-hidden">
                    <div className={`absolute left-0 top-0 h-full ${isDone ? "bg-[#00FFAA] w-full" : isActive ? "bg-[#00D4FF]" : "bg-transparent"}`}
                      style={isActive ? { animation: `loadBar ${s.dur}ms linear forwards` } : { width: isDone ? "100%" : 0 }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Telemetry */}
        <div className="mt-12 holo-panel p-6 relative scan-line" data-testid="telemetry-panel">
          <div className="label-overline mb-4">{t("enc.telemetry")}</div>
          <div className="font-mono-data text-[12px] text-[#00D4FF] space-y-1 max-h-48 overflow-hidden">
            {telemetry.map((line, i) => (<div key={i} className="opacity-90">{line}</div>))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes loadBar { from { width: 0% } to { width: 100% } }
      `}</style>
    </div>
  );
}
